import { useState } from 'react';
import { fold, formatQuantity, type RecipeIngredient } from '@familyhub/shared';
import { Sheet } from '../platform/Sheet';
import { isOffline } from '../api/client';
import { addToShoppingList } from './shopping';

/**
 * "Naar boodschappenlijst" — pick the ingredients you need (scaled to the chosen
 * servings) and send them to the groceries list in one call. Pantry basics are
 * pre-unticked so the common case is one tap. The measure and the recipe name go
 * into each item's note, since the shopping list stores a count, not a unit.
 *
 * For a recipe generated from the pakket box, the box produce (`have`) is
 * pre-unticked too — the household already has it, so the list is for the
 * *additional* items to buy.
 */

const STAPLES = ['zout', 'peper', 'water', 'olie', 'boter', 'suiker'];
const isStaple = (name: string): boolean => STAPLES.some((s) => name.toLowerCase().includes(s));

/** Fold a phrase to inflection-stable word tokens (never a decompounder). */
const foldTokens = (s: string): string[] => s.toLowerCase().split(/\s+/).map(fold).filter(Boolean);

/**
 * Is this ingredient already in the box the household has? True when a whole box
 * item's folded words all appear in the ingredient name — so "courgette" matches
 * "courgettes" but "ui" never matches "bosui" (fold does not split compounds).
 */
function inBox(name: string, have: string[]): boolean {
  if (have.length === 0) return false;
  const nameTokens = new Set(foldTokens(name));
  return have.some((h) => {
    const hs = foldTokens(h);
    return hs.length > 0 && hs.every((t) => nameTokens.has(t));
  });
}

function amountLabel(ing: RecipeIngredient): string {
  if (ing.quantity == null) return '';
  return ing.unit ? `${formatQuantity(ing.quantity)} ${ing.unit}` : formatQuantity(ing.quantity);
}

export function AddToListSheet({
  title,
  ingredients,
  have,
  onClose,
}: {
  title: string;
  ingredients: RecipeIngredient[];
  /**
   * Items the household already has — the pakket box a generated recipe drew on.
   * Pre-unticked, because the add-to-list is for the *additional* things to buy.
   */
  have?: string[];
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(
    () =>
      new Set(
        ingredients
          .map((_, i) => i)
          .filter((i) => !isStaple(ingredients[i]!.name) && !inBox(ingredients[i]!.name, have ?? [])),
      ),
  );
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'offline' | 'error'>('idle');
  const [addedCount, setAddedCount] = useState(0);

  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const confirm = async () => {
    const items = ingredients
      .filter((_, i) => selected.has(i))
      .map((ing) => {
        const amt = amountLabel(ing);
        return { name: ing.name, quantity: 1, note: amt ? `${amt} — ${title}` : title };
      });
    if (items.length === 0) return;
    setStatus('saving');
    try {
      const res = await addToShoppingList(items);
      setAddedCount(res.added.length);
      setStatus('done');
    } catch (err) {
      setStatus(isOffline(err) ? 'offline' : 'error');
    }
  };

  if (status === 'done') {
    return (
      <Sheet title="Toegevoegd" onClose={onClose}>
        <p className="sheet-note">
          {addedCount} {addedCount === 1 ? 'ingrediënt' : 'ingrediënten'} op de boodschappenlijst gezet.
        </p>
        <div className="sheet-row">
          <button className="btn btn-primary" onClick={onClose}>Klaar</button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title="Naar boodschappenlijst" onClose={onClose}>
      <p className="sheet-note">
        {have && have.length > 0
          ? 'Basics en wat al in je pakket zit, staan uit.'
          : 'Basics zoals zout en olie staan uit.'}
      </p>
      <ul className="addlist">
        {ingredients.map((ing, i) => (
          <li key={i}>
            <label className="addlist-row">
              <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
              <span className="ing-amt">{amountLabel(ing)}</span>
              <span className="ing-name">{ing.name}</span>
            </label>
          </li>
        ))}
      </ul>

      {status === 'offline' && <p className="sheet-note error">Geen verbinding — dit lukt alleen online.</p>}
      {status === 'error' && <p className="sheet-note error">Er ging iets mis. Probeer het opnieuw.</p>}

      <div className="sheet-row" style={{ marginTop: 4 }}>
        <button
          className="btn btn-primary"
          onClick={() => void confirm()}
          disabled={selected.size === 0 || status === 'saving'}
        >
          {status === 'saving' ? 'Bezig…' : `Toevoegen (${selected.size})`}
        </button>
      </div>
    </Sheet>
  );
}
